import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { UserPlus, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';
import { cn } from '@/lib/utils';
import { avatarUrl } from '@/common/utils/avatarUrl';
import { organizationQueries, organizationQueryKeys } from '@/features/organizations/queries';
import { OrganizationService } from '@/features/organizations/services/OrganizationService';
import { RoleBadge } from '@/features/organizations/components/role-badge/RoleBadge';
import { useInviteMemberDialog } from '@/features/organizations/components/invite-member-dialog/useInviteMemberDialog';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';
import type { OrgRole } from '@/features/organizations/services/schemas';

type Props = {
  orgId: number;
};

export function MembersTab({ orgId }: Props) {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();
  const openInviteDialog = useInviteMemberDialog();
  const { data: members } = useSuspenseQuery(organizationQueries.members(orgId));
  const currentUser = useCurrentUser();

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: number; role: OrgRole }) =>
      OrganizationService.updateMemberRole(orgId, memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: number) => OrganizationService.removeMember(orgId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationQueryKeys.all });
    },
  });

  const handleRemoveMember = async (memberId: number, name: string) => {
    const confirmed = await confirm({
      title: t`Remove member?`,
      description: t`${name} will lose access to all supply points of this organization.`,
      confirmLabel: t`Remove`,
    });
    if (confirmed) removeMutation.mutate(memberId);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <Plural value={members.length} one="# member" other="# members" />
        </p>
        <Button onClick={() => openInviteDialog(orgId)} className="gap-1.5">
          <UserPlus size={14} />
          <Trans>Invite member</Trans>
        </Button>
      </div>

      <div className="rounded-2xl border border-border overflow-hidden bg-card">
        <div className="hidden sm:grid grid-cols-[1fr_160px_140px_48px] gap-4 px-5 py-3 bg-muted/50 border-b border-border text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>
            <Trans>User</Trans>
          </span>
          <span>
            <Trans>Role</Trans>
          </span>
          <span>
            <Trans>Status</Trans>
          </span>
          <span />
        </div>
        {members.map((member, idx) => (
          <div
            key={member.id}
            className={cn(
              'flex sm:grid sm:grid-cols-[1fr_160px_140px_48px] gap-3 sm:gap-4 items-center px-5 py-3.5',
              idx < members.length - 1 ? 'border-b border-border' : ''
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="size-8 rounded-full bg-muted overflow-hidden shrink-0 border border-border">
                <img
                  src={avatarUrl(member.user.firstName, member.user.lastName, member.user.email)}
                  alt={
                    [member.user.firstName, member.user.lastName].filter(Boolean).join(' ') ||
                    member.user.email
                  }
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium text-foreground truncate">
                    {member.user.firstName} {member.user.lastName}
                  </span>
                  {member.user.email === currentUser.email && (
                    <span className="text-[10px] font-semibold text-primary bg-primary/10 border border-primary/20 px-1.5 py-0.5 rounded-md">
                      <Trans>Me</Trans>
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">{member.user.email}</div>
              </div>
            </div>
            <div>
              {member.user.email === currentUser.email ? (
                <RoleBadge role={member.role} />
              ) : (
                <Select
                  value={member.role}
                  onValueChange={(v) =>
                    updateRoleMutation.mutate({ memberId: member.id, role: v as OrgRole })
                  }
                  disabled={updateRoleMutation.isPending}
                >
                  <SelectTrigger className="h-8 text-xs w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">
                      <Trans>Admin</Trans>
                    </SelectItem>
                    <SelectItem value="write">
                      <Trans>Editor</Trans>
                    </SelectItem>
                    <SelectItem value="read">
                      <Trans>Reader</Trans>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="hidden sm:flex" />
            <div className="flex justify-end">
              {member.user.email !== currentUser.email && (
                <button
                  onClick={() =>
                    handleRemoveMember(
                      member.id,
                      `${member.user.firstName} ${member.user.lastName}`.trim()
                    )
                  }
                  className="rounded-lg p-1.5 text-muted-foreground/40 hover:bg-destructive/10 hover:text-destructive transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
